import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { EditOutlined } from '@ant-design/icons';
import { message, Button, Input, Modal, Typography, Form, Collapse } from 'antd';
import DOMPurify from 'dompurify';
import {
    useCreateGlossaryTermMutation,
    useCreateGlossaryNodeMutation,
} from '../../../../graphql/glossaryTerm.generated';
import { EntityType } from '../../../../types.generated';
import { useEntityRegistry } from '../../../useEntityRegistry';
import NodeParentSelect from './NodeParentSelect';
import { useEntityData, useRefetch } from '../EntityContext';
import analytics, { EventType } from '../../../analytics';
import DescriptionModal from '../components/legacy/DescriptionModal';
import { validateCustomUrnId } from '../../../shared/textUtil';
import { useGlossaryEntityData } from '../GlossaryEntityContext';
import { getGlossaryRootToUpdate, updateGlossarySidebar } from '../../../glossary/utils';
import { useTranslation } from '../../../../i18n/useTranslation';

const StyledItem = styled(Form.Item)`
    margin-bottom: 0;
`;

const OptionalWrapper = styled.span`
    font-weight: normal;
`;

const StyledButton = styled(Button)`
    padding: 0;
`;

interface Props {
    entityType: EntityType;
    onClose: () => void;
    refetchData?: () => void;
}

function CreateGlossaryEntityModal(props: Props) {
    const { t } = useTranslation();
    const { entityType, onClose, refetchData } = props;
    const entityData = useEntityData();
    const { isInGlossaryContext, urnsToUpdate, setUrnsToUpdate } = useGlossaryEntityData();
    const [form] = Form.useForm();
    const entityRegistry = useEntityRegistry();
    const [stagedId, setStagedId] = useState<string | undefined>(undefined);
    const [stagedName, setStagedName] = useState('');
    const [subType, setSubType] = useState('');
    const [selectedParentUrn, setSelectedParentUrn] = useState(entityData.urn);
    const [documentation, setDocumentation] = useState('');
    const [isDocumentationModalVisible, setIsDocumentationModalVisible] = useState(false);
    const [createButtonDisabled, setCreateButtonDisabled] = useState(true);
    const refetch = useRefetch();

    const [createGlossaryTermMutation] = useCreateGlossaryTermMutation();
    const [createGlossaryNodeMutation] = useCreateGlossaryNodeMutation();

    function createGlossaryEntity() {
        const mutation =
            entityType === EntityType.GlossaryTerm ? createGlossaryTermMutation : createGlossaryNodeMutation;

        const sanitizedDescription = DOMPurify.sanitize(documentation);
        mutation({
            variables: {
                input: {
                    id: stagedId?.length ? stagedId : undefined,
                    name: stagedName,
                    subType,
                    parentNode: selectedParentUrn || null,
                    description: sanitizedDescription || null,
                    isEditable: true,
                },
            },
        })
            .then(() => {
                /**
                 * Only close the modal once the entity is successfully created. On failure (e.g. a
                 * duplicate name) we keep the modal open with the entered values so the user can correct
                 * the name and resubmit without re-entering everything.
                 */
                onClose();
                message.loading({ content: t('Updating...'), duration: 2 });
                setTimeout(() => {
                    analytics.event({
                        type: EventType.CreateGlossaryEntityEvent,
                        entityType,
                        parentNodeUrn: selectedParentUrn || undefined,
                    });
                    message.success({
                        content: `${t('CREATED_ENTITY_TITLE', {
                            entityName: entityRegistry.getEntityName(entityType),
                        })}`,
                        duration: 2,
                    });
                    refetch();
                    if (isInGlossaryContext) {
                        // either refresh this current glossary node or the root nodes or root terms
                        const nodeToUpdate = entityData?.urn || getGlossaryRootToUpdate(entityType);
                        updateGlossarySidebar([nodeToUpdate], urnsToUpdate, setUrnsToUpdate);
                    }
                    if (refetchData) {
                        refetchData();
                    }
                }, 2000);
            })
            .catch((e) => {
                message.destroy();
                message.error({ content: `${t('Failed to create:')}\n${e.message || ''}`, duration: 3 });
            });
    }

    function addDocumentation(description: string) {
        setDocumentation(description);
        setIsDocumentationModalVisible(false);
    }

    return (
        <Modal
            title={t('CREATE_ENTITY_TITLE', { entityName: t(entityRegistry.getEntityName(entityType) || '') })}
            visible
            centered
            className="f-modal"
            onCancel={onClose}
            footer={
                <>
                    <Button onClick={onClose} type="default">
                        {t('Cancel')}
                    </Button>
                    <Button
                        type="primary"
                        onClick={createGlossaryEntity}
                        disabled={createButtonDisabled}
                        data-testid="glossary-entity-modal-create-button"
                    >
                        {t('Create')}
                    </Button>
                </>
            }
        >
            <Form
                form={form}
                initialValues={{
                    parent: selectedParentUrn,
                }}
                layout="vertical"
                onFieldsChange={() =>
                    setCreateButtonDisabled(form.getFieldsError().some((field) => field.errors.length > 0))
                }
            >
                <Form.Item label={<Typography.Text strong>{t('Name')}</Typography.Text>}>
                    <StyledItem
                        data-testid="create-glossary-entity-modal-name"
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: t('ENTER_ENTITY_NAME_MESSAGE', {
                                    entityName: entityRegistry.getEntityName(entityType),
                                }),
                            },
                            { whitespace: true },
                            { min: 1, max: 100 },
                        ]}
                        hasFeedback
                    >
                        <Input
                            autoFocus
                            aria-label={t('Name')}
                            placeholder={t('Enter Name')}
                            value={stagedName}
                            onChange={(event) => setStagedName(event.target.value)}
                        />
                    </StyledItem>
                </Form.Item>
                <Form.Item
                    label={
                        <Typography.Text strong>
                            {t('Parent')} <OptionalWrapper>({t('optional')})</OptionalWrapper>
                        </Typography.Text>
                    }
                >
                    <StyledItem name="parent">
                        <NodeParentSelect
                            selectedParentUrn={selectedParentUrn}
                            setSelectedParentUrn={setSelectedParentUrn}
                        />
                    </StyledItem>
                </Form.Item>
                <Form.Item
                    label={
                        <Typography.Text strong>
                            {t('SubType')} <OptionalWrapper>({t('optional')})</OptionalWrapper>
                        </Typography.Text>
                    }
                >
                    <StyledItem
                        data-testid="create-glossary-entity-modal-name"
                        name="subtype"
                        hasFeedback
                        rules={[
                            {
                                required: false,
                                message: t('Enter SubType.'),
                            },
                            { whitespace: true },
                            { min: 1, max: 100 },
                        ]}
                    >
                        <Input
                            autoFocus
                            aria-label="SubType"
                            value={subType}
                            placeholder={t('Enter SubType')}
                            onChange={(event) => setSubType(event.target.value)}
                        />
                    </StyledItem>
                </Form.Item>
                <StyledItem
                    label={
                        <Typography.Text strong>
                            {t('Documentation')} <OptionalWrapper>({t('optional')})</OptionalWrapper>
                        </Typography.Text>
                    }
                >
                    <StyledButton
                        type="link"
                        onClick={() => setIsDocumentationModalVisible(true)}
                        aria-label={`${documentation ? t('Edit') : t('Add')} ${t('Documentation')}`}
                    >
                        <EditOutlined />
                        {documentation ? t('Edit') : t('Add')} {t('Documentation')}
                    </StyledButton>
                    {isDocumentationModalVisible && (
                        <DescriptionModal
                            title={t('Add Documentation')}
                            onClose={() => setIsDocumentationModalVisible(false)}
                            onSubmit={addDocumentation}
                            description={documentation}
                        />
                    )}
                </StyledItem>
                <Collapse ghost>
                    <Collapse.Panel
                        header={<Typography.Text type="secondary">{t('Advanced')}</Typography.Text>}
                        key="1"
                    >
                        <Form.Item
                            label={
                                <Typography.Text strong>
                                    {t(entityRegistry.getEntityName(props.entityType) || '')} {t('Id')}
                                </Typography.Text>
                            }
                        >
                            <Typography.Paragraph>
                                {t(
                                    "By default, a random UUID will be generated to uniquely identify this entity. If you'd like to provide a custom id, you may provide it here. Note that it should be unique across the entire Glossary. Be careful, you cannot easily change the id after creation.",
                                )}
                            </Typography.Paragraph>
                            <Form.Item
                                name="id"
                                rules={[
                                    () => ({
                                        validator(_, value) {
                                            if (value && validateCustomUrnId(value)) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error(t('Please enter a valid entity id')));
                                        },
                                    }),
                                ]}
                            >
                                <Input
                                    placeholder={t('Classification')}
                                    onChange={(event) => setStagedId(event.target.value)}
                                />
                            </Form.Item>
                        </Form.Item>
                    </Collapse.Panel>
                </Collapse>
            </Form>
        </Modal>
    );
}

export default CreateGlossaryEntityModal;
